import { Link, usePage } from "@inertiajs/react"
import { cn } from "@/lib/utils"
import { SeoHead } from "@/components/SeoHead"
import { organizationNode } from "@/lib/seo"
import type { PageProps } from "@/types/inertia"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"
import { track } from "@/lib/analytics"
import { PARTNERSHIP, PRODUCTION, SETUP } from "@/lib/offers"

// Unlisted pricing sheet at /pricing, sent by hand. noindex, and absent from
// sitemap/llms.txt/nav/footer — nothing on the site links here.
//
// Deliberately NOT added to robots.txt: that file is world-readable, so a
// Disallow line would advertise this URL's existence, and blocking the crawl
// would stop crawlers from ever reading the noindex tag below.
const DESCRIPTION =
  "How a FullCast engagement is priced: the Podcast Growth Partnership and the Production retainer, what each covers, and what setup includes."

// PARTNERSHIP, PRODUCTION and SETUP live in @/lib/offers so /proposal pages
// describe the same two packages in the same words.

// Two bands. Production is deliberately near-identical across tiers: the whole
// price difference is the strategy layer, and the table should show that
// rather than implying the cheaper tier is a stripped-down product.
const EPISODE_ROWS: [string, string, string][] = [
  ["Weekly video episode — full edit, mastering, intro/outro and music", "✓", "✓"],
  ["Show notes with summary, timestamps, pull quotes and links", "✓", "✓"],
  ["Custom artwork built from your guest's headshot", "✓", "✓"],
  ["Episode page on your site, SEO-optimized, player embedded", "✓", "✓"],
  ["Distribution to Apple, Spotify and every other directory", "✓", "✓"],
  ["Cross-posting to LinkedIn, YouTube and your other channels", "✓", "✓"],
  ["Guest promo email, written and ready to send", "✓", "✓"],
]

const STRATEGY_ROWS: [string, string, string][] = [
  ["Authority strategy — the show's business role, audience and positioning, set before recording starts", "✓", "—"],
  ["Relationship map — the categories of people the show is built to reach", "✓", "—"],
  ["Guest pipeline intelligence — researched and prioritized opportunities each month, with outreach templates", "✓", "—"],
  ["Conversation system — host briefs, guest notes, interview questions, episode angles", "✓", "—"],
  ["Content multiplication — clips, posts and assets built from every conversation", "✓", "show notes & social only"],
  ["Monthly session — strategy, guest pipeline and what to do next, reviewed together", "✓ review", "report only"],
]


function Cell({ value }: { value: string }) {
  const isYes = value.startsWith("✓")
  return (
    <td
      className={cn(
        "border-b border-hairline px-3 py-3 text-center align-top text-sm",
        isYes ? "font-semibold text-accent-display" : "text-ink-muted",
      )}
    >
      {value}
    </td>
  )
}

export default function Pricing() {
  const base = (usePage<PageProps>().props.app_url || "https://fullcast.co").replace(/\/$/, "")
  const handleApplyClick = () => track("CTA Click")

  return (
    <MarketingLayout>
      <SeoHead
        title="Two ways to work together"
        description={DESCRIPTION}
        noindex
        jsonLd={[organizationNode(base)]}
      />

      <Section containerClassName="max-w-3xl">
        <Eyebrow>FullCast · Engagement</Eyebrow>
        <h1 className="display-1 mt-4">Two ways to work together</h1>
        <p className="mt-5 text-lg text-ink-body">
          The Growth Partnership treats the show as a system for authority and
          relationships — strategy, guest intelligence and multiplication around
          every recording. The Production retainer handles everything around the
          recording itself. Both ship weekly, on a schedule that doesn't depend
          on your week going well.
        </p>
      </Section>

      {/* The two tiers */}
      <Section className="border-t border-hairline bg-surface">
        <div className="grid items-start gap-5 md:grid-cols-2">
          <div className="rounded-xl border border-accent bg-accent-faded p-7">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-accent-display">
              The full partnership
            </div>
            <h2 className="mt-2">Podcast Growth Partnership</h2>
            <p className="mt-2 text-sm text-ink-muted">
              For established experts who want the show to build authority and
              relationships, not just publish episodes.
            </p>
            <div className="mt-6 border-b border-hairline pb-5 tabular-nums">
              <span className="font-display text-4xl font-semibold text-ink-display">$5,000</span>
              <span className="text-ink-muted"> / month</span>
              <p className="mt-2 text-sm text-ink-muted">
                Six-month engagement
                <br />
                plus $5,000 one-time setup — reduced for existing shows
              </p>
            </div>
            <ul className="mt-5 space-y-2">
              {PARTNERSHIP.map(([name, detail]) => (
                <li key={name} className="text-sm text-ink-body">
                  <strong>{name}</strong> — {detail}
                </li>
              ))}
            </ul>
            <p className="mt-6 border-t border-hairline pt-4 text-sm text-ink-muted">
              <strong className="text-ink-display">You own:</strong> showing up to
              record, approving guests, sending the invitations, and the follow-up.
              Those land better coming from you.
            </p>
          </div>

          <div className="rounded-xl border border-hairline bg-page p-7">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-muted">
              Production retainer
            </div>
            <h2 className="mt-2">Podcast Production</h2>
            <p className="mt-2 text-sm text-ink-muted">
              For hosts who already know what the show is for, and want the
              production and publishing lifted off their plate.
            </p>
            <div className="mt-6 border-b border-hairline pb-5 tabular-nums">
              <span className="font-display text-4xl font-semibold text-ink-display">$3,000</span>
              <span className="text-ink-muted"> / month</span>
              <p className="mt-2 text-sm text-ink-muted">
                Month to month
                <br />
                plus $5,000 one-time setup — reduced for existing shows
              </p>
            </div>
            <ul className="mt-5 space-y-2">
              {PRODUCTION.map((item) => (
                <li key={item} className="text-sm text-ink-body">
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-6 border-t border-hairline pt-4 text-sm text-ink-muted">
              <strong className="text-ink-display">You own:</strong> the show's
              direction, who's on it, and everything that happens off the mic.
            </p>
          </div>
        </div>
      </Section>

      {/* Side by side */}
      <Section className="border-t border-hairline">
        <div className="max-w-2xl">
          <Eyebrow>Side by side</Eyebrow>
          <h2 className="display-2 mt-4">What each tier covers</h2>
        </div>

        <div className="mt-10 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left">
            <thead>
              <tr>
                <th className="border-b-2 border-ink-display px-3 py-3 text-xs font-semibold uppercase tracking-[0.1em] text-ink-muted">
                  What you get
                </th>
                <th className="w-40 border-b-2 border-ink-display px-3 py-3 text-center text-xs font-semibold uppercase tracking-[0.1em] text-ink-muted">
                  Growth Partnership
                  <br />
                  $5,000/mo
                </th>
                <th className="w-40 border-b-2 border-ink-display px-3 py-3 text-center text-xs font-semibold uppercase tracking-[0.1em] text-ink-muted">
                  Production
                  <br />
                  $3,000/mo
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={3} className="border-b border-hairline px-3 pb-2 pt-6 text-xs font-semibold uppercase tracking-[0.1em] text-accent-display">
                  Every episode
                </td>
              </tr>
              {EPISODE_ROWS.map(([label, a, b]) => (
                <tr key={label}>
                  <td className="border-b border-hairline px-3 py-3 align-top text-sm text-ink-body">{label}</td>
                  <Cell value={a} />
                  <Cell value={b} />
                </tr>
              ))}
              <tr>
                <td colSpan={3} className="border-b border-hairline px-3 pb-2 pt-6 text-xs font-semibold uppercase tracking-[0.1em] text-accent-display">
                  Strategy &amp; growth
                </td>
              </tr>
              {STRATEGY_ROWS.map(([label, a, b]) => (
                <tr key={label}>
                  <td className="border-b border-hairline px-3 py-3 align-top text-sm text-ink-body">{label}</td>
                  <Cell value={a} />
                  <Cell value={b} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Setup */}
      <Section className="border-t border-hairline bg-surface">
        <div className="max-w-2xl">
          <Eyebrow>Setup</Eyebrow>
          <h2 className="display-2 mt-4">Included in both tiers</h2>
          <p className="mt-5 text-lg text-ink-body">
            $5,000 one-time, before the first episode ships.{" "}
            <strong>Already running a show?</strong> Setup is reviewed and
            reduced — several of these steps are already behind you.
          </p>
        </div>

        <ul className="mt-10 grid gap-x-10 gap-y-1 sm:grid-cols-2">
          {SETUP.map((item) => (
            <li key={item} className="text-ink-body">
              {item}
            </li>
          ))}
        </ul>
      </Section>

      {/* How it runs */}
      <Section className="border-t border-hairline" containerClassName="max-w-3xl">
        <Eyebrow>How it runs</Eyebrow>
        <h2 className="display-2 mt-4">What the first months look like</h2>
        <p className="mt-5 text-lg text-ink-body">
          Setup takes about six weeks from kickoff to the first episode. After
          that the cycle repeats weekly: you record, we produce, the episode
          ships. Questions get answered within one business day.
        </p>
        <p className="mt-4 text-ink-muted">
          On the <strong>Growth Partnership</strong>, that weekly rhythm sits
          inside a longer one. Each month brings a fresh set of researched guest
          opportunities and a strategic review of what's working. Six months,
          because positioning and guest relationships compound rather than spike
          — you can renew at the end.
        </p>
        <p className="mt-4 text-ink-muted">
          On <strong>Production</strong>, the retainer covers the machine around
          the recording. You decide what the show is for and who's on it;
          everything from the raw file onward is handled.
        </p>
        <p className="mt-4 text-ink-muted">
          One boundary worth naming on both tiers: we build the guest pipeline,
          you own the relationship. We research and prioritize who's worth
          talking to and write the outreach for you — but the invitation itself
          goes out from you or your team, because that's what makes it land.
        </p>
      </Section>

      {/* Close */}
      <Section className="border-t border-hairline bg-surface" containerClassName="max-w-3xl">
        <h2 className="display-2">Next step</h2>
        <p className="mt-5 text-lg text-ink-body">
          A short call to confirm which tier fits and what your first eight weeks
          look like.
        </p>
        <div className="mt-8">
          <Link
            href="/apply"
            onClick={handleApplyClick}
            className="inline-block rounded-xl bg-ink-display px-6 py-3 text-page no-underline hover:bg-ink-body"
          >
            Apply for a Fit Call →
          </Link>
        </div>
      </Section>
    </MarketingLayout>
  )
}
